import type { BigNumber } from "../BigNumber.js";
import type { DisplayMode } from "../types.js";

/** a~z(소문자 26) + A~Z(대문자 26) = 52자. bijective base-52 인덱싱 기반. */
const LETTERS = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";

/**
 * 1000-base 그룹 인덱스(>=1) → 알파벳 접미사. bijective numeration이라
 * 1=a … 26=z → 27=A … 52=Z → 53=aa … (z 다음이 aa, 단순 base-52와 다름).
 */
export function alphabetSymbol(group: number): string {
  let k = group;
  let out = "";
  while (k > 0) {
    k -= 1;
    out = LETTERS[k % 52]! + out;
    k = Math.floor(k / 52);
  }
  return out;
}

/** alphabetSymbol의 역 — 접미사 문자열 → 1000-base 그룹 인덱스(>=1). 비-알파벳 문자 포함 시 null. */
export function alphabetGroup(sym: string): number | null {
  if (sym === "") return null;
  let g = 0;
  for (const ch of sym) {
    const idx = LETTERS.indexOf(ch);
    if (idx < 0) return null;
    g = g * 52 + (idx + 1);
  }
  return g;
}

/**
 * 1000-base 공통 표기 — exponent를 그룹 인덱스로 접미사 매핑.
 * 1000 미만(exponent<=0)은 정수 표기, 그 이상은 `mantissa.toFixed(d) + 접미사`.
 * 모드 고유 단위(`SYMBOLS`) 소진 시 알파벳 fallback.
 */
abstract class PowerOfThousandBase implements DisplayMode {
  /** 인덱스 0 = 10^3, 1 = 10^6, … 소진 시 알파벳으로 자동 fallback. */
  protected abstract readonly symbols: readonly string[];

  format(value: BigNumber, decimals: number): string {
    if (value.isZero()) return "0";
    if (value.exponent <= 0) return Math.round(value.toNumber()).toString();
    return value.mantissa.toFixed(decimals) + this.symbolFor(value.exponent);
  }

  private symbolFor(group: number): string {
    const s = this.symbols;
    if (group <= s.length) return s[group - 1]!;
    return alphabetSymbol(group);
  }
}

/**
 * 영미권 short-scale 단위 심볼 배열 SSoT. index 0 = K(10^3) … index 20 = V(10^63).
 * `parseUnits.ts`의 역파싱 매핑도 이 배열을 참조해 이중 정의를 방지한다.
 */
export const SHORT_SCALE_SYMBOLS = [
  "K", "M", "B", "T", "Qa", "Qi", "Sx", "Sp", "Oc", "No", "Dc",
  "Ud", "Dd", "Td", "Qad", "Qid", "Sxd", "Spd", "Od", "Nd", "V",
] as const;

/**
 * 영미권 short-scale 단위 (방치형 게임 표준). 1000-base, 10^9 = "B".
 * K=thousand, M=million, B=billion, T=trillion, Qa=quadrillion … V=vigintillion (21단위, 10^3~10^63).
 * 소진 시 알파벳 fallback. (구 KmgDisplayMode의 SI 접두사 K/M/G/T…에서 교체 — 레퍼런스 idleblade ShortScale 정합.)
 */
export class ShortScaleDisplayMode extends PowerOfThousandBase {
  protected readonly symbols = SHORT_SCALE_SYMBOLS;
}

/** 알파벳 a~z → A~Z → aa…. */
export class AlphabetDisplayMode extends PowerOfThousandBase {
  protected readonly symbols = [] as const; // 항상 alphabetSymbol fallback 경로
}

/**
 * 아랍어 단위 약자 (1000-base) — ShortScale의 아랍어 대응. RTL UI에서 단위가 아랍어로 보이게 한다.
 * أ=ألف(10^3), م=مليون(10^6), ب=بليون(10^9), ت=تريليون(10^12). 이후는 충돌 없는 아랍 문자 순차 (21단위, 10^63).
 * 숫자 자릿수는 BiDi 표준대로 LTR 유지. 소진 시 알파벳 fallback.
 */
export class ArabicDisplayMode extends PowerOfThousandBase {
  protected readonly symbols = [
    "أ", "م", "ب", "ت", "ث", "ج", "ح", "خ", "د", "ذ", "ر",
    "ز", "س", "ش", "ص", "ض", "ط", "ظ", "ع", "غ", "ف",
  ] as const;
}
